import React, { useState, useRef, useEffect } from "react";
import { makeStyles, useTheme, Popper, Dropdown, MenuIcon } from "@seahorse/ui";
import {
  BESTX_EARLIEST_EXECUTION_MS,
  BESTX_EXECUTION_RANGE_ERROR,
  getDefaultBestXExecutionTimestamp,
  getDefaultBestXExpiryTimestamp,
} from "./bestxDateDefaults";
import {
  fromDateInputValue,
  fromZonedDateTimeInputValue,
  toUtcDateTimeValue,
  getDateTimeInputZoneLabel,
  getExecutionTimestampBounds,
  toDateInputValue,
  toZonedDateTimeInputValue,
} from "../shared/dateTimeInput";
import DisplayTimeZoneDropdown from "../shared/DisplayTimeZoneDropdown";
import {
  useDisplayTimeZone,
  useResetDisplayTimeZoneOnPairChange,
} from "../../Utils/displayTimeZone";

interface BestXPopOverMenuProps {
  useContext: boolean;
  currencyPair: string;
  currencyList: string[];
  tenorList: string[] | null;
  setTenor: any | null;
  setUserLanguage: any;
  userLanguage: string;
  executionTimestamp: string;
  executionSpotRate: number | null;
  executionForwardPoints: number | null;
  expirationTimestamp: string;
  onSubmit: (trade: {
    currencyPair: string;
    useContext: boolean;
    executionTimestamp: string;
    executionSpotRate: number | null;
    executionForwardPoints: number | null;
    expirationTimestamp: string;
  }) => void;
}

const BestXPopOverMenu: React.FunctionComponent<BestXPopOverMenuProps> = ({
  useContext,
  currencyPair,
  setUserLanguage,
  userLanguage,
  currencyList,
  tenorList,
  setTenor,
  executionTimestamp,
  executionSpotRate,
  executionForwardPoints,
  expirationTimestamp,
  onSubmit,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  useResetDisplayTimeZoneOnPairChange(currencyPair);
  const [selectedCurrency, setSelectedCurrency] = useState(
    useContext ? "AUTO" : currencyPair || "AUTO"
  );
  const menuIconRef: any = useRef(null);
  const popperRef: any = useRef(null);

  const [selectedTenors, setSelectedTenors]: any = useState([]);

  // Draft completion time is held in UTC; the input shows the display zone.
  const [draftExecTs, setDraftExecTs] = useState(
    () =>
      executionTimestamp ||
      localStorage.getItem("bestx_exec_ts") ||
      getDefaultBestXExecutionTimestamp()
  );
  const [draftExecRate, setDraftExecRate] = useState(() =>
    executionSpotRate !== null
      ? String(executionSpotRate)
      : localStorage.getItem("bestx_exec_rate") || "157.96"
  );
  const [draftExecFwd, setDraftExecFwd] = useState(() =>
    executionForwardPoints !== null
      ? String(executionForwardPoints)
      : localStorage.getItem("bestx_exec_fwd") || "-0.0002"
  );
  const [draftExpiry, setDraftExpiry] = useState(() =>
    toDateInputValue(
      expirationTimestamp ||
        localStorage.getItem("bestx_expiry") ||
        getDefaultBestXExpiryTimestamp()
    )
  );
  const { timeZone } = useDisplayTimeZone();
  const draftExecInput = toZonedDateTimeInputValue(draftExecTs, timeZone);
  const { minExecutionTimestamp, maxExecutionTimestamp } =
    getExecutionTimestampBounds(timeZone, BESTX_EARLIEST_EXECUTION_MS);
  const [draftErrors, setDraftErrors] = useState<Record<string, string>>({
    executionTimestamp: "",
    expirationTimestamp: "",
  });

  const theme = useTheme();
  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      padding: "10px",
      zIndex: 1000,

      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    menuIcon: {
      cursor: "pointer",
      color: theme.globallink.colors.Slate[700],
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px",
      border: 0,
      borderRadius: "4px",
      background: "transparent",
      "&:hover": {
        color: "#E0E0E0",
        background: "rgba(255,255,255,0.08)",
      },
      "&:focus-visible": {
        outline: "2px solid #4C9AFF",
        outlineOffset: "2px",
      },
    },
    popperContainer: {
      padding: "16px",
      backgroundColor: theme.globallink.colors.Slate[900],
      borderRadius: "8px",
      boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.35)",
      maxWidth: "320px",
      zIndex: 1000,
      width: "320px",
      color: "#E0E0E0",

    },
    dropdownContainer: { marginBottom: "12px" },
    title: {
      color: "#E0E0E0",
      fontSize: "14px",
      marginBottom: "6px",
      fontWeight: "bold",
      opacity: 0.9,
    },
    input: {
      width: "100%",
      padding: "10px",
      borderRadius: "8px",
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      outline: "none",
      marginBottom: "12px",
    },
    buttonRow: {
      display: "flex",
      gap: 10,
      justifyContent: "flex-end",
      marginTop: 8,
    },
    button: {
      cursor: "pointer",
      padding: "10px 12px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(80, 170, 255, 0.16)",
      color: "#E0E0E0",
      fontWeight: 700,
    },
    secondaryButton: {
      cursor: "pointer",
      padding: "10px 12px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      fontWeight: 600,
    },
    error: {
      color: "#ff6b6b",
      fontSize: 12,
      marginTop: -8,
      marginBottom: 8,
    },
  });

  const classes = useStyles();

  const [dropDownCurrencyList, setDropDownCurrencyList] = useState([
    { label: "Auto", value: "AUTO" },
    { label: "USDJPY", value: "USDJPY" },
  ]);

  useEffect(() => {
    if (currencyList !== null) {
      const currencyItems = [...currencyList]
        .sort((a, b) => a.localeCompare(b))
        .map((currency) => ({
          label: currency,
          value: currency,
        }));
      setDropDownCurrencyList([{ label: "Auto", value: "AUTO" }, ...currencyItems]);
    }
  }, [currencyList]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check containment synchronously — dropdown portals may remove
      // the clicked option from the DOM before a setTimeout fires.
      const insidePopper =
        popperRef.current && popperRef.current.contains(target);
      const insideMenuIcon =
        menuIconRef.current && menuIconRef.current.contains(target);

      if (insidePopper || insideMenuIcon) return;

      // Also ignore clicks on elements that live inside a portal
      // (e.g. Dropdown option lists) rendered by the component library.
      const clickedEl = target as HTMLElement;
      if (clickedEl.closest?.("[role='listbox'], [role='option'], [role='presentation']")) return;

      setIsModalOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    return () => document.removeEventListener("mousedown", handleOutsideClick, true);
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "user_language") {
        setUserLanguage(localStorage.getItem("user_language") || "English");
      }
      if (event.key === "default_currency") {
        setSelectedCurrency(localStorage.getItem("default_currency") || "AUTO");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [setUserLanguage]);

  useEffect(() => {
    if (tenorList !== null && selectedTenors.length === 0) {
      const mapped = tenorList.map((item) => ({ label: item, value: item }));
      setSelectedTenors(mapped);
      setTenor?.(tenorList);
    }
  }, [tenorList, selectedTenors.length, setTenor]);

  useEffect(() => {
    if (isModalOpen) return;

    const nextSelectedCurrency = useContext ? "AUTO" : currencyPair || "AUTO";
    setSelectedCurrency(nextSelectedCurrency);
    setDraftExecTs(executionTimestamp || "");
    setDraftExecRate(
      executionSpotRate !== null ? String(executionSpotRate) : ""
    );
    setDraftExecFwd(
      executionForwardPoints !== null ? String(executionForwardPoints) : ""
    );
    setDraftExpiry(toDateInputValue(expirationTimestamp || ""));
  }, [
    isModalOpen,
    useContext,
    currencyPair,
    executionTimestamp,
    executionSpotRate,
    executionForwardPoints,
    expirationTimestamp,
  ]);

  const handleCurrencyChange = (value: any) => {
    setSelectedCurrency(value);
  };

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {
      executionTimestamp: "",
      expirationTimestamp: "",
    };

    if (!draftExecInput) {
      nextErrors.executionTimestamp = "Completion time is required.";
    } else if (
      draftExecInput < minExecutionTimestamp ||
      draftExecInput > maxExecutionTimestamp
    ) {
      nextErrors.executionTimestamp = BESTX_EXECUTION_RANGE_ERROR;
    }

    if (
      draftExpiry &&
      draftExecInput &&
      draftExpiry < toDateInputValue(draftExecInput)
    ) {
      nextErrors.expirationTimestamp =
        "Value date must be on or after the completion time.";
    }

    if (nextErrors.executionTimestamp || nextErrors.expirationTimestamp) {
      setDraftErrors(nextErrors);
      return;
    }

    setDraftErrors({ executionTimestamp: "", expirationTimestamp: "" });

    const executionTimestamp = toUtcDateTimeValue(draftExecTs);
    const expirationTimestamp = fromDateInputValue(draftExpiry);
    const parsedExecRate = Number(draftExecRate);
    const parsedExecFwd = Number(draftExecFwd);

    onSubmit({
      currencyPair:
        selectedCurrency === "AUTO" ? currencyPair : selectedCurrency,
      useContext: selectedCurrency === "AUTO",
      executionTimestamp,
      executionSpotRate: Number.isFinite(parsedExecRate)
        ? parsedExecRate
        : null,
      executionForwardPoints: Number.isFinite(parsedExecFwd)
        ? parsedExecFwd
        : null,
      expirationTimestamp,
    });

    // persist (optional, but handy)
    localStorage.setItem("bestx_exec_ts", executionTimestamp);
    localStorage.setItem("bestx_exec_rate", draftExecRate);
    localStorage.setItem("bestx_exec_fwd", draftExecFwd);
    localStorage.setItem("bestx_expiry", expirationTimestamp);

    setIsModalOpen(false);
  };

  const handleReset = () => {
    setSelectedCurrency(useContext ? "AUTO" : currencyPair || "AUTO");
    setDraftExecTs(executionTimestamp || "");
    setDraftExecRate(executionSpotRate !== null ? String(executionSpotRate) : "");
    setDraftExecFwd(
      executionForwardPoints !== null ? String(executionForwardPoints) : ""
    );
    setDraftExpiry(toDateInputValue(expirationTimestamp || ""));
    setDraftErrors({ executionTimestamp: "", expirationTimestamp: "" });
  };

  return (
    <div className={classes.container}>
      <button
        type="button"
        ref={menuIconRef}
        className={classes.menuIcon}
        onClick={() => setIsModalOpen((prev) => !prev)}
        aria-label="Trade settings"
        title="Trade settings"
      >
        <MenuIcon />
      </button>

      <Popper
        open={isModalOpen}
        anchorEl={menuIconRef.current}
        placement="bottom-start"
        ref={popperRef}
      >
        <div className={classes.popperContainer}>
          {/* Display preference: applies immediately, independent of Submit. */}
          <DisplayTimeZoneDropdown
            currencyPair={currencyPair}
            titleClassName={classes.title}
            containerClassName={classes.dropdownContainer}
          />

          {/* Currency */}
          <div className={classes.title}>Currency Pair</div>
          <div className={classes.dropdownContainer}>
            <Dropdown
              items={dropDownCurrencyList}
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              allowBlankSelection={true}
              placeholder="Select currency pair"
              fullWidth={true}
            />
          </div>

          {/* NEW trade fields */}
          <div className={classes.title}>
            Completion Time ({getDateTimeInputZoneLabel(draftExecTs, timeZone)})
          </div>
          <input
            className={classes.input}
            type="datetime-local"
            step={60}
            min={minExecutionTimestamp}
            max={maxExecutionTimestamp}
            value={draftExecInput}
            onChange={(e) => {
              setDraftExecTs(fromZonedDateTimeInputValue(e.target.value, timeZone));
              setDraftErrors((prev) => ({
                ...prev,
                executionTimestamp: "",
                expirationTimestamp: "",
              }));
            }}
          />
          {draftErrors.executionTimestamp && (
            <div className={classes.error}>{draftErrors.executionTimestamp}</div>
          )}

          <div className={classes.title}>Execution Spot Rate</div>
          <input
            className={classes.input}
            value={draftExecRate}
            onChange={(e) => setDraftExecRate(e.target.value)}
            placeholder="e.g. 157.96"
            inputMode="decimal"
          />
          <div className={classes.title}>Execution Forward Points</div>
          <input
            className={classes.input}
            value={draftExecFwd}
            onChange={(e) => setDraftExecFwd(e.target.value)}
            placeholder="e.g. -0.0002"
            inputMode="decimal"
          />

          <div className={classes.title}>Value Date</div>
          <input
            className={classes.input}
            type="date"
            min={toDateInputValue(draftExecInput) || undefined}
            value={draftExpiry}
            onChange={(e) => {
              setDraftExpiry(e.target.value);
              setDraftErrors((prev) => ({ ...prev, expirationTimestamp: "" }));
            }}
          />
          {draftErrors.expirationTimestamp && (
            <div className={classes.error}>{draftErrors.expirationTimestamp}</div>
          )}

          {/* Language — disabled until supported */}
          {/* {userLanguage && (
            <>
              <div className={classes.title}>Language</div>
              <Dropdown
                items={languageList}
                value={userLanguage}
                onChange={handleUserLanguageChange}
                allowBlankSelection={true}
                placeholder="Select language"
                fullWidth={true}
              />
            </>
          )} */}

          {/* Submit */}
          <div className={classes.buttonRow}>
            <button className={classes.secondaryButton} onClick={handleReset} type="button">
              Reset
            </button>
            <button className={classes.button} onClick={handleSubmit} type="button">
              Submit
            </button>
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default BestXPopOverMenu;
